import { useEffect, useMemo, useRef, useState, type ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { Archive, Copy, ExternalLink, FileText, Loader2, Search, Settings, Shield, ShieldCheck, UserPlus, Users, X } from "lucide-react";
import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@nexus/ui";
import { cn } from "@nexus/ui";
import type { UserItem, UserAccessEntry } from "@/features/identity-hub/api/identity-hub.api";
import { isSuperTenantMember } from "@/features/identity-hub/utils/iam.utils";
import { timeAgo } from "@/features/identity-hub/utils/time";
import { statusBadgeClass } from "@/features/identity-hub/utils/status";
import { OrgLogoMark } from "./OrgLogoMark";
import { UserPhotoMark } from "./UserPhotoMark";
import { ImagePreviewTrigger } from "./ImagePreviewTrigger";
import { SuperTenantBadge } from "./SuperTenantBadge";
import type { RoleRow } from "./RolesTable";
import { usePermissions, useIsOwnAccessGroup } from "@/features/auth/hooks/usePermissions";
import { permissionPayloadToKeys, unmatchedPermissionTuples } from "@/features/identity-hub/hooks/usePermissionSelection";
import { PermissionAccessCards } from "./PermissionAccessCards";
import { AuditTimeline, useAuditLogBadgeCount } from "./AuditTimeline";
import { ArchivedBadge, RoleTypeBadge, YourRoleBadge } from "./shared/RoleBadges";
import { RowActionsTrigger } from "./shared/RowActionsTrigger";
import { useDrawerClose } from "./shared/drawer/useDrawerClose";
import { DrawerTabStrip } from "./shared/drawer/DrawerTabStrip";
import { DrawerStat } from "./shared/drawer/DrawerStat";
import { IamOverlayDrawer } from "./shared/drawer/IamOverlayDrawer";
import { MemberActionsMenu } from "./shared/drawer/MemberActionsMenu";

// ─── Tabs ─────────────────────────────────────────────────────────────────────

export type RoleDrawerTab = "members" | "permissions" | "audit" | "settings";

/** Fits the four tabs with counts on one row; the 920px org floor exists for the org tab row only. */
const ROLE_DRAWER_MIN_WIDTH_PX = 560;

// ─── Members tab ─────────────────────────────────────────────────────────────

function MembersTab({
  members,
  accessByUser,
  onJumpToPerson,
  onRemoveMember,
  isRemovingMember,
  removingMemberId,
  onEditProfile,
  onSuspend,
  onUnsuspend,
  onOpenAuditLog,
  isLoading,
  hasMore,
  isFetchingMore,
  onLoadMore,
}: {
  members: UserItem[];
  accessByUser: Map<string, UserAccessEntry[]>;
  onJumpToPerson: (userId: string) => void;
  onRemoveMember?: (userId: string) => void;
  isRemovingMember?: boolean;
  removingMemberId?: string | null;
  onEditProfile?: (user: UserItem) => void;
  onSuspend?: (user: UserItem) => void;
  onUnsuspend?: (user: UserItem) => void;
  onOpenAuditLog?: (user: UserItem) => void;
  isLoading?: boolean;
  hasMore?: boolean;
  isFetchingMore?: boolean;
  onLoadMore?: () => void;
}) {
  const { t } = useTranslation();

  // Members are fetched server-side by accessId and paginated (see the role
  // members query in IdentityHubDashboard). The sentinel auto-loads the next
  // page as the user scrolls the drawer body, so the list reflects the role's
  // true membership rather than only the users on the loaded People page.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            onLoadMore?.();
            break;
          }
        }
      },
      { rootMargin: "200px 0px" },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [hasMore, onLoadMore, members.length]);

  if (isLoading && members.length === 0) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (members.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-10 text-center">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
          <Users className="h-5 w-5 text-muted-foreground" />
        </div>
        <p className="text-sm font-medium">{t("iam.roleDrawer.members.emptyTitle")}</p>
        <p className="text-xs text-muted-foreground">{t("iam.roleDrawer.members.emptyBody")}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      {members.map((user) => (
        <div
          key={user.id}
          className="group/member flex items-center gap-3 rounded-lg border border-border px-3 py-2.5 transition-colors hover:bg-muted/40"
        >
          {/* Clickable area → jump to person */}
          <div
            role="button"
            tabIndex={0}
            onClick={() => onJumpToPerson(user.id)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onJumpToPerson(user.id); } }}
            className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 text-left"
          >
            <ImagePreviewTrigger src={user.photoUrl} alt={user.displayName}>
              <UserPhotoMark name={user.displayName} photoUrl={user.photoUrl} size="sm" className="shrink-0" />
            </ImagePreviewTrigger>
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <div className="flex min-w-0 items-center gap-1.5">
                <span className="truncate text-[13px] font-medium leading-tight text-foreground">
                  {user.displayName}
                </span>
                {isSuperTenantMember(accessByUser.get(user.id)) && <SuperTenantBadge />}
              </div>
              <span className="truncate text-[11px] leading-tight text-muted-foreground">
                {user.email}
              </span>
            </div>
          </div>

          {/* Status badge */}
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className={cn(
                    "shrink-0 rounded-full border px-1.5 py-0.5 text-[10px] font-medium",
                    statusBadgeClass(user.status),
                  )}
                >
                  {t(`iam.personDrawer.status.${user.status}`, user.status)}
                </span>
              </TooltipTrigger>
              <TooltipContent side="left" className="max-w-[220px]">
                {t(`iam.personDrawer.statusDescription.${user.status}`, {
                  defaultValue: t("iam.personDrawer.statusDescription.default", "Account status."),
                })}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Remove from role */}
          {onRemoveMember && (() => {
            const isLastAccess = user.accessIds.length <= 1;
            const isRemoving = isRemovingMember && removingMemberId === user.id;
            return (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className={cn("opacity-0 group-hover/member:opacity-100", isRemoving && "opacity-100")}>
                      <button
                        type="button"
                        onClick={() => !isLastAccess && !isRemoving && onRemoveMember(user.id)}
                        disabled={isLastAccess || isRemoving}
                        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md transition-opacity disabled:cursor-not-allowed disabled:opacity-40 hover:bg-destructive/10 hover:text-destructive"
                        aria-label={t("iam.roleDrawer.members.removeFromRole", { name: user.displayName })}
                        aria-busy={isRemoving}
                      >
                        {isRemoving ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin text-destructive" />
                        ) : (
                          <X className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
                        )}
                      </button>
                    </span>
                  </TooltipTrigger>
                  {isLastAccess && (
                    <TooltipContent side="left">
                      {t("iam.roleDrawer.members.lastAccessTooltip", "Can't revoke — this is the user's only access")}
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            );
          })()}

          {/* Actions dropdown */}
          <MemberActionsMenu
            user={user}
            onEditProfile={onEditProfile}
            onOpenAuditLog={onOpenAuditLog}
            onSuspend={onSuspend}
            onUnsuspend={onUnsuspend}
          />
        </div>
      ))}
      {hasMore && (
        <div
          ref={sentinelRef}
          className="flex items-center justify-center py-2 text-[11px] text-muted-foreground"
        >
          {isFetchingMore ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            t("iam.roleDrawer.members.loadingMore", {
              defaultValue: "Loading more…",
            })
          )}
        </div>
      )}
    </div>
  );
}

// ─── Permissions tab ──────────────────────────────────────────────────────────

type RawPerm = { type: string; method: string; route: string };

function PermissionsTab({ permissions }: { permissions: RawPerm[] }) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const selected = useMemo(() => permissionPayloadToKeys(permissions), [permissions]);
  // Tuples the IAM catalog does not know about — a renamed or retired route.
  // The cards can only render catalog entries, so list these separately rather
  // than letting the drawer imply the role has fewer grants than it does.
  const uncatalogued = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return unmatchedPermissionTuples(permissions)
      .filter((perm) => !query || `${perm.method} ${perm.route}`.toLowerCase().includes(query));
  }, [permissions, searchQuery]);

  if (permissions.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-10 text-center">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
          <Shield className="h-5 w-5 text-muted-foreground" />
        </div>
        <p className="text-sm font-medium">{t("iam.roleDrawer.permissions.emptyTitle")}</p>
        <p className="text-xs text-muted-foreground">{t("iam.roleDrawer.permissions.emptyBody")}</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={t("iam.roleDrawer.permissions.searchPlaceholder")}
          className="h-7 w-full rounded-md border border-border bg-background pl-7 pr-3 text-[12px] outline-none focus:border-ring focus:ring-1 focus:ring-ring/40 placeholder:text-muted-foreground"
        />
      </div>
      <PermissionAccessCards
        className="min-h-0 flex-1"
        selected={selected}
        onTogglePerm={() => undefined}
        onToggleGroup={() => undefined}
        onRemoveKeys={() => undefined}
        filter={searchQuery}
        showSelectedOnly={false}
        readOnly
      />
      {uncatalogued.length > 0 && (
        <div className="flex max-h-32 shrink-0 flex-col gap-2 overflow-y-auto rounded-lg border border-border p-3">
          <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            {t("iam.roleDrawer.permissions.uncataloguedTitle", "Not in the catalog")}
          </p>
          <ul className="flex flex-col gap-1">
            {uncatalogued.map((perm) => (
              <li key={`${perm.type}|${perm.method}|${perm.route}`} className="flex items-center gap-2 text-[12px]">
                <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                  {perm.method}
                </span>
                <span className="truncate text-foreground">{perm.route}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ─── Settings tab ─────────────────────────────────────────────────────────────

function SettingsTab({ role }: { role: RoleRow }) {
  const { t } = useTranslation();
  const isSystem = role.type === "system";
  const isArchived = role.isArchived;

  const readOnlyMessage = isArchived
    ? t(
        "iam.roleDrawer.settings.archivedCatalogOnly",
        "This role is archived. Restore or manage it from the Roles catalog.",
      )
    : isSystem
      ? t(
          "iam.roleDrawer.settings.systemReadOnly",
          "Built-in system roles are managed by Nexus and cannot be edited here.",
        )
      : t(
          "iam.roleDrawer.settings.catalogOnly",
          "Name, description, and permissions are edited from the Roles catalog.",
        );

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-start gap-2 rounded-md border border-info-subtle-foreground/25 bg-info-subtle px-3 py-2.5 text-xs font-medium text-info-subtle-foreground">
        {isArchived ? (
          <Archive className="mt-px h-4 w-4 shrink-0" aria-hidden />
        ) : (
          <ShieldCheck className="mt-px h-4 w-4 shrink-0" aria-hidden />
        )}
        <span>{readOnlyMessage}</span>
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-medium text-muted-foreground">{t("iam.roleDrawer.settings.roleName")}</label>
        <input
          type="text"
          value={role.name}
          readOnly
          disabled
          className="h-8 rounded-md border border-border bg-background px-3 text-sm text-foreground outline-none disabled:cursor-not-allowed disabled:opacity-50"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-medium text-muted-foreground">{t("iam.roleDrawer.settings.description")}</label>
        <textarea
          value={role.description ?? ""}
          readOnly
          disabled
          rows={3}
          className="rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground outline-none resize-none disabled:cursor-not-allowed disabled:opacity-50"
        />
      </div>

      <div className="flex flex-col gap-3">
        <p className="text-xs font-medium text-muted-foreground">
          {t("iam.roleDrawer.settings.security", "Security")}
        </p>
        <div className="rounded-lg border border-dashed border-border bg-muted/10 px-3 py-2.5">
          <p className="text-[13px] font-medium text-muted-foreground">
            {t("iam.roleDrawer.settings.notifyOnAssignment", "Notify on assignment")}
          </p>
          <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground/70">
            {t(
              "iam.roleDrawer.settings.notifyOnAssignmentHint",
              "Email admins when a member is added to this role",
            )}
          </p>
          <p className="mt-1.5 text-[11px] font-medium text-muted-foreground/60">
            {t("iam.roleDrawer.settings.notApplicableYet", "Not applicable yet")}
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── RoleDrawer ───────────────────────────────────────────────────────────────

export interface RoleDrawerProps {
  role: RoleRow | null;
  /** Tab shown when the drawer opens or switches role. */
  initialTab?: RoleDrawerTab;
  members: UserItem[];
  /** Members are loaded server-side by accessId and paginated inside the drawer. */
  membersLoading?: boolean;
  hasMoreMembers?: boolean;
  isFetchingMoreMembers?: boolean;
  onLoadMoreMembers?: () => void;
  permissions: Array<{ type: string; method: string; route: string }>;
  allUsers: UserItem[];
  accessByUser: Map<string, UserAccessEntry[]>;
  onClose: () => void;
  onJumpToPerson: (userId: string) => void;
  onRemoveMember?: (userId: string) => void;
  onAddMember?: (userId: string) => void;
  isAddingMember?: boolean;
  addingMemberId?: string | null;
  isRemovingMember?: boolean;
  removingMemberId?: string | null;
  onEditProfile?: (user: UserItem) => void;
  onSuspend?: (user: UserItem) => void;
  onUnsuspend?: (user: UserItem) => void;
  onOpenAuditLog?: (options?: { highlightId?: string | null; searchPrefill?: string | null; category?: string | null }) => void;
}

export function RoleDrawer({ role, initialTab = "members", members, membersLoading, hasMoreMembers, isFetchingMoreMembers, onLoadMoreMembers, permissions, allUsers, accessByUser, onClose, onJumpToPerson, onRemoveMember, onAddMember, isAddingMember, addingMemberId, isRemovingMember, removingMemberId, onEditProfile, onSuspend, onUnsuspend, onOpenAuditLog }: RoleDrawerProps) {
  const { t } = useTranslation();
  const iamPerms = usePermissions();
  const canViewAuditLog = iamPerms.IDENTITY_HUB_VIEW_AUDIT_LOG.isOn;
  const isOwnAccessGroup = useIsOwnAccessGroup(role?.id ?? "");
  const [tab, setTab] = useState<RoleDrawerTab>(initialTab);
  const { visible, handleClose, resetVisible } = useDrawerClose(onClose);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [addMemberSearch, setAddMemberSearch] = useState("");
  const prevAddingMember = useRef(false);

  useEffect(() => {
    resetVisible();
    setTab(initialTab);
    setAddMemberOpen(false);
    setAddMemberSearch("");
  }, [role?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (prevAddingMember.current && !isAddingMember) {
      setAddMemberOpen(false);
      setAddMemberSearch("");
    }
    prevAddingMember.current = !!isAddingMember;
  }, [isAddingMember]);

  const eligibleUsers = useMemo(() => {
    if (!role) return [];
    return allUsers.filter((u) => {
      const entries = accessByUser.get(u.id) ?? [];
      return !entries.some((e) => e.accessId === role.id);
    });
  }, [allUsers, accessByUser, role]);

  const filteredEligible = useMemo(() => {
    const q = addMemberSearch.trim().toLowerCase();
    if (!q) return eligibleUsers;
    return eligibleUsers.filter(
      (u) => u.displayName.toLowerCase().includes(q) || u.email.toLowerCase().includes(q),
    );
  }, [eligibleUsers, addMemberSearch]);


  // NOTE: this hook must run on every render (incl. when `role` is null) — early-returning
  // before it would change the hook count and crash with "Rendered fewer hooks than expected".
  const auditCount = useAuditLogBadgeCount("access", role?.id, { enabled: canViewAuditLog });

  if (!role) return null;

  const tabs: Array<{ id: RoleDrawerTab; label: string; Icon: ComponentType<{ className?: string }>; count?: number }> = [
    { id: "members",     label: t("iam.roleDrawer.tabs.members"),     Icon: Users,     count: role.memberCount },
    { id: "permissions", label: t("iam.roleDrawer.tabs.permissions"), Icon: Shield,    count: permissions.length },
    { id: "audit",       label: t("iam.roleDrawer.tabs.audit"),       Icon: FileText,  count: auditCount },
    { id: "settings",    label: t("iam.roleDrawer.tabs.settings"),    Icon: Settings },
  ];

  const RoleIcon = role.type === "system" ? ShieldCheck : Shield;

  return (
    <IamOverlayDrawer
      open={visible}
      onOpenChange={(next) => { if (!next) handleClose(); }}
      title={role.name}
      minWidthPx={ROLE_DRAWER_MIN_WIDTH_PX}
    >
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-card">
        {/* ── Head ─────────────────────────────────────────── */}
        <div className="flex items-start justify-between gap-2.5 border-b border-border/40 px-5 py-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted">
              <RoleIcon className="h-4.5 w-4.5 text-muted-foreground" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3
                  className={cn(
                    "truncate text-[15px] font-semibold leading-tight",
                    role.isArchived && "line-through decoration-muted-foreground/60",
                  )}
                >
                  {role.name}
                </h3>
                <RoleTypeBadge type={role.type} />
                {role.isArchived && <ArchivedBadge variant="outline" />}
                {isOwnAccessGroup && <YourRoleBadge />}
              </div>
              <div className="flex items-center gap-1.5">
                <ImagePreviewTrigger src={role.tenantLogoUrl} alt={role.tenantName}>
                  <OrgLogoMark name={role.tenantName} logoUrl={role.tenantLogoUrl} size={14} />
                </ImagePreviewTrigger>
                <p className="text-[11px] text-muted-foreground">{role.tenantName}</p>
              </div>
              {role.description && (
                <p className="mt-1 text-[11px] leading-snug text-muted-foreground/70">{role.description}</p>
              )}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <RowActionsTrigger />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuItem
                      className="gap-2"
                      onClick={() => navigator.clipboard.writeText(role.id)}
                    >
                      <Copy className="h-3.5 w-3.5" />
                      {t("iam.roleDrawer.copyId")}
                    </DropdownMenuItem>
                  </TooltipTrigger>
                  <TooltipContent side="left" className="max-w-[220px]">
                    {t("iam.roleDrawer.copyIdTooltip")}
                  </TooltipContent>
                </Tooltip>
                {onOpenAuditLog && canViewAuditLog && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuItem
                        className="gap-2"
                        onClick={() => onOpenAuditLog({ searchPrefill: `${role.name} ${role.tenantName}`, category: "access" })}
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                        {t("iam.roleDrawer.viewAuditLog")}
                      </DropdownMenuItem>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="max-w-[220px]">
                      {t("iam.roleDrawer.viewAuditLogTooltip")}
                    </TooltipContent>
                  </Tooltip>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <button
              type="button"
              onClick={handleClose}
              className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label={t("iam.roleDrawer.closePanel")}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {/* ── Stats row ─────────────────────────────────────── */}
        <div className="grid grid-cols-3 border-b border-border">
          <DrawerStat label={t("iam.roleDrawer.stats.members")} tooltip={t("iam.roleDrawer.stats.membersTooltip")}>
            {role.memberCount}
          </DrawerStat>
          <DrawerStat
            label={t("iam.roleDrawer.stats.permissions")}
            tooltip={role.name === "Super Admin"
              ? t("iam.roleDrawer.stats.permissionsTooltipUnlimited")
              : t("iam.roleDrawer.stats.permissionsTooltip")}
          >
            {role.name === "Super Admin" ? "∞" : role.permissionCount}
          </DrawerStat>
          <DrawerStat label={t("iam.roleDrawer.stats.updated")} tooltip={t("iam.roleDrawer.stats.updatedTooltip")} valueClassName="text-sm font-semibold">
            <span title={role.updatedAt}>{timeAgo(role.updatedAt)}</span>
          </DrawerStat>
        </div>

        {/* ── Tab strip ─────────────────────────────────────── */}
        <DrawerTabStrip tabs={tabs} activeTab={tab} onTabChange={setTab} />

        {/* ── Body ──────────────────────────────────────────── */}
        <div
          className={cn(
            "flex min-h-0 flex-1 flex-col gap-3 px-4 py-3",
            tab === "permissions" ? "overflow-hidden" : "overflow-y-auto",
          )}
        >
          {tab === "members" && (
            <MembersTab
              members={members}
              isLoading={membersLoading}
              hasMore={hasMoreMembers}
              isFetchingMore={isFetchingMoreMembers}
              onLoadMore={onLoadMoreMembers}
              accessByUser={accessByUser}
              onJumpToPerson={onJumpToPerson}
              onRemoveMember={onRemoveMember}
              isRemovingMember={isRemovingMember}
              removingMemberId={removingMemberId}
              onEditProfile={onEditProfile}
              onSuspend={onSuspend}
              onUnsuspend={onUnsuspend}
              onOpenAuditLog={onOpenAuditLog && canViewAuditLog ? (user) => onOpenAuditLog({ searchPrefill: user.email }) : undefined}
            />
          )}

          {tab === "permissions" && (
            <PermissionsTab permissions={permissions} />
          )}

          {tab === "audit" && (
            <AuditTimeline
              scope="access"
              scopeId={role.id}
              hasPermission={canViewAuditLog}
              onEntryClick={onOpenAuditLog ? (id) => onOpenAuditLog({ highlightId: id }) : undefined}
            />
          )}

          {tab === "settings" && (
            <SettingsTab role={role} />
          )}
        </div>

        {/* ── Add member panel ──────────────────────────────── */}
        {addMemberOpen && (
          <div className="animate-in slide-in-from-bottom-2 fade-in duration-200 border-t border-border bg-muted px-4 py-3">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-[11px] font-medium text-muted-foreground">
                {t("iam.roleDrawer.addMember.title", { role: role.name })}
              </p>
              <button
                type="button"
                onClick={() => setAddMemberOpen(false)}
                className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            </div>

            {/* Search */}
            <div className="relative mb-2">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={addMemberSearch}
                onChange={(e) => setAddMemberSearch(e.target.value)}
                placeholder={t("iam.roleDrawer.addMember.searchPlaceholder")}
                className="h-7 w-full rounded-md border border-border bg-background pl-7 pr-3 text-[12px] outline-none focus:border-ring focus:ring-1 focus:ring-ring/40 placeholder:text-muted-foreground"
              />
            </div>

            {/* User list */}
            <div className="flex max-h-52 flex-col gap-1 overflow-y-auto">
              {filteredEligible.length === 0 ? (
                <p className="py-4 text-center text-[11px] text-muted-foreground">
                  {t("iam.roleDrawer.addMember.empty")}
                </p>
              ) : (
                filteredEligible.map((user) => {
                  const isAddingUser = isAddingMember && addingMemberId === user.id;
                  return (
                  <div
                    key={user.id}
                    role="button"
                    tabIndex={isAddingMember ? -1 : 0}
                    aria-disabled={isAddingMember}
                    aria-busy={isAddingUser}
                    onClick={() => {
                      if (isAddingMember) return;
                      onAddMember?.(user.id);
                    }}
                    onKeyDown={(e) => {
                      if (isAddingMember) return;
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onAddMember?.(user.id);
                      }
                    }}
                    className={cn(
                      "flex cursor-pointer items-center gap-2.5 rounded-lg border border-border bg-card px-3 py-2 text-left transition-colors hover:border-primary/40 hover:bg-primary/5",
                      isAddingMember && "cursor-not-allowed",
                      isAddingUser && "border-primary/40 bg-primary/5",
                      isAddingMember && !isAddingUser && "opacity-50",
                    )}
                  >
                    <ImagePreviewTrigger src={user.photoUrl} alt={user.displayName}>
                      <UserPhotoMark name={user.displayName} photoUrl={user.photoUrl} size="sm" className="shrink-0" />
                    </ImagePreviewTrigger>
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <div className="flex min-w-0 items-center gap-1.5">
                        <span className="truncate text-[13px] font-medium leading-tight">{user.displayName}</span>
                        {isSuperTenantMember(accessByUser.get(user.id)) && <SuperTenantBadge />}
                      </div>
                      <span className="truncate text-[11px] leading-tight text-muted-foreground">{user.email}</span>
                      {(() => {
                        const current = (accessByUser.get(user.id) ?? []).find(
                          (entry) => entry.access.tenantId === role.tenantId,
                        );
                        return current ? (
                          <span className="truncate text-[11px] leading-tight text-muted-foreground">
                            {t("iam.roleDrawer.addMember.switchFrom", {
                              role: current.access.name,
                              defaultValue: "Switch from {{role}}",
                            })}
                          </span>
                        ) : null;
                      })()}
                    </div>
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span
                            className={cn(
                              "flex shrink-0 items-center justify-center",
                              isAddingUser
                                ? "h-5 w-5"
                                : "rounded-full border px-1.5 py-0.5 text-[10px] font-medium",
                              !isAddingUser && statusBadgeClass(user.status),
                            )}
                          >
                            {isAddingUser ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
                            ) : (
                              t(`iam.personDrawer.status.${user.status}`, user.status)
                            )}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="left" className="max-w-[220px]">
                          {t(`iam.personDrawer.statusDescription.${user.status}`, {
                            defaultValue: t("iam.personDrawer.statusDescription.default", "Account status."),
                          })}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* ── Footer ────────────────────────────────────────── */}
        {tab !== "settings" && (
          <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-3">
            <Button
              size="sm"
              className="gap-1.5"
              onClick={() => setAddMemberOpen((prev) => !prev)}
              disabled={isAddingMember || role.isArchived}
            >
              {isAddingMember ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <UserPlus className="h-3.5 w-3.5" />
              )}
              {t("iam.roleDrawer.addMemberCta")}
            </Button>
          </div>
        )}
      </div>
    </IamOverlayDrawer>
  );
}
